'use client';
import React from 'react';

import { ForgeBadge } from '@/lib/services/forgeGoalsService';

interface BadgeCelebrationProps {
  badge: ForgeBadge;
  nextGoalTitle?: string;
  onClose: () => void;
  onSetNextGoal: () => void;
}

export default function BadgeCelebration({ badge, nextGoalTitle, onClose, onSetNextGoal }: BadgeCelebrationProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm bg-card border border-primary/40 rounded-2xl overflow-hidden shadow-2xl text-center">
        {/* Glow header */}
        <div className="bg-gradient-to-b from-primary/30 to-transparent px-6 pt-8 pb-4">
          {/* Badge icon */}
          <div className="relative inline-flex items-center justify-center mb-4">
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl scale-150" />
            <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 border-2 border-primary/60 flex items-center justify-center">
              <span className="text-5xl">🏆</span>
            </div>
          </div>
          <div className="text-xs text-primary font-bold tracking-widest uppercase mb-1">Goal Achieved</div>
          <h2 className="text-2xl font-black tracking-wider text-foreground">GOAL FORGED</h2>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Badge details */}
          <div className="bg-card-elevated rounded-xl p-4 border border-primary/20">
            <div className="text-sm font-bold text-foreground mb-1">{badge.title}</div>
            {badge.description && (
              <div className="text-xs text-muted-foreground">{badge.description}</div>
            )}
            <div className="text-2xs text-primary/70 mt-2 font-bold uppercase tracking-wider">
              Earned {new Date(badge.earnedAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>

          {/* Next goal prompt */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
            <div className="text-xs text-primary font-bold uppercase tracking-wider mb-1">Next Forged Goal</div>
            {nextGoalTitle ? (
              <div className="text-sm text-foreground font-medium">{nextGoalTitle}</div>
            ) : (
              <div className="text-sm text-muted-foreground">Set your next challenge — the forge never stops.</div>
            )}
          </div>

          <div className="text-xs text-muted-foreground italic">
            &ldquo;The forge reveals the truth. Measure first. Decide second.&rdquo;
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex flex-col gap-2">
          <button
            onClick={onSetNextGoal}
            className="w-full btn-forge-primary py-3 rounded-xl text-sm font-black tracking-wider"
          >
            SET NEXT GOAL ⚡
          </button>
          <button
            onClick={onClose}
            className="w-full btn-forge-ghost py-2.5 rounded-xl text-sm font-bold"
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
}
